/**
 * GuaranteeMultiSelect — wrapper de MultiSelect pour les codes de garanties.
 *
 * Particularité : on groupe par `GUARANTEES.group` (vie, amc, dentaire, vue,
 * invalidite, bien_etre) pour faciliter la sélection. Les labels sont
 * traduits via i18n (label_fr / label_en de la constante).
 *
 * Pourquoi un wrapper plutôt que MultiSelect direct ? Parce que la
 * traduction des groupes et des labels est métier, et qu'on veut que
 * l'appelant n'ait à passer que `value` + `onChange`.
 */

import { useTranslation } from "react-i18next";
import { GUARANTEES, type GuaranteeCode } from "@kollectivia/shared/constants";

import { MultiSelect, type MultiSelectOption } from "./MultiSelect";

type Props = {
  value: readonly GuaranteeCode[];
  onChange: (next: GuaranteeCode[]) => void;
  id?: string;
};

export function GuaranteeMultiSelect({ value, onChange, id }: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith("en") ? "en" : "fr";

  const options: MultiSelectOption<GuaranteeCode>[] = GUARANTEES.map((g) => ({
    value: g.code,
    label: lang === "en" ? g.label_en : g.label_fr,
    group: g.group,
    groupLabel: t(`projects.guarantees.groups.${g.group}`),
  }));

  return (
    <MultiSelect<GuaranteeCode>
      id={id}
      options={options}
      value={value}
      onChange={onChange}
      placeholder={t("projects.guarantees.add_placeholder")}
    />
  );
}
